import { capitaliseFirstLetter } from "@shared/translations/src";
import { startOfMonth, endOfMonth, format } from "date-fns";

/**
 * Array of all month names in lowercase format.
 * Used as the source of truth for month values in URLs and internal logic.
 *
 * @example
 * ```ts
 * MONTHS[0] // "january"
 * MONTHS[11] // "december"
 * ```
 */
export const MONTHS = [
  "january",
  "february",
  "march",
  "april",
  "may",
  "june",
  "july",
  "august",
  "september",
  "october",
  "november",
  "december",
] as const;

/**
 * Union type of all valid month names in lowercase format.
 * Derived from the MONTHS constant to maintain type safety.
 *
 * @example
 * ```ts
 * const month: Month = "january"; // ✅ Valid
 * const invalid: Month = "jan"; // ❌ Type error
 * ```
 */
export type Month = (typeof MONTHS)[number];

/**
 * Converts a lowercase month name to a capitalized display format.
 *
 * @param month - The month name in lowercase format
 * @returns The month name with the first letter capitalized
 *
 * @example
 * ```ts
 * getMonthDisplayName("january") // "January"
 * getMonthDisplayName("december") // "December"
 * ```
 */
export function getMonthDisplayName(month: Month): string {
  return capitaliseFirstLetter(month);
}

/**
 * Calculates the date range for a given month and year.
 *
 * Uses date-fns to handle leap years and month boundaries correctly.
 * Returns ISO date strings suitable for API queries.
 *
 * @param month - The month name in lowercase format
 * @param year - The year (defaults to current year if not provided)
 * @returns Object containing fromDate and toDate as ISO strings (YYYY-MM-DD)
 * @throws {Error} If an invalid month is provided (should not happen with proper typing)
 *
 * @example
 * ```ts
 * // Get date range for February 2024 (leap year)
 * getMonthDateRange("february", 2024)
 * // Returns: { fromDate: "2024-02-01", toDate: "2024-02-29" }
 *
 * // Get date range for February 2023 (non-leap year)
 * getMonthDateRange("february", 2023)
 * // Returns: { fromDate: "2023-02-01", toDate: "2023-02-28" }
 *
 * // Use current year
 * getMonthDateRange("january")
 * // Returns: { fromDate: "2025-01-01", toDate: "2025-01-31" }
 * ```
 */
export function getMonthDateRange(month: Month, year: number = new Date().getFullYear()) {
  const monthIndex = MONTHS.indexOf(month);

  if (monthIndex === -1) {
    throw new Error(`Invalid month: ${month}`);
  }

  // Create a date for the first day of the specified month
  const monthStart = new Date(year, monthIndex, 1);

  return {
    fromDate: format(startOfMonth(monthStart), "yyyy-MM-dd"),
    toDate: format(endOfMonth(monthStart), "yyyy-MM-dd"),
  };
}

/**
 * Gets the current month name based on today's date.
 *
 * Returns a Result type for safe error handling, though in practice this should
 * never fail since Date.getMonth() always returns a valid index (0-11).
 *
 * @returns Result containing the current month name or an error
 *
 * @example
 * ```ts
 * const result = getCurrentMonth();
 * if (result.success) {
 *   console.log(result.data); // "january", "february", etc.
 * } else {
 *   console.error(result.error); // Should never happen in practice
 * }
 *
 * // With fallback handling
 * const currentMonth = result.success ? result.data : "january";
 * ```
 */
export function getCurrentMonth(): Result<Month, string> {
  const currentMonthIndex = new Date().getMonth();
  const month = MONTHS[currentMonthIndex];

  if (!month) {
    return { success: false, error: `Invalid month index: ${currentMonthIndex}` };
  }

  return { success: true, data: month };
}

/**
 * Creates a combined month+year string for display.
 */
export function getMonthYearDisplay(month: Month, year: number): string {
  return `${getMonthDisplayName(month)} ${year}`;
}

/**
 * Creates a month+year value from month and year in ISO format.
 */
export function createMonthYearValue(month: Month, year: number): string {
  const monthIndex = MONTHS.indexOf(month) + 1;
  return `${year}-${String(monthIndex).padStart(2, "0")}`;
}

/**
 * Parses a month+year value from ISO format back into month and year.
 */
export function parseMonthYearValue(value: string): { month: Month; year: number } | null {
  const parts = value.split("-");
  if (parts.length !== 2) return null;

  const [yearString, monthString] = parts;
  if (!yearString || !monthString) return null;

  const year = parseInt(yearString, 10);
  const monthIndex = parseInt(monthString, 10);

  if (isNaN(year) || isNaN(monthIndex)) return null;
  if (monthIndex < 1 || monthIndex > 12) return null;

  const month = MONTHS[monthIndex - 1];
  if (!month) return null;

  return { month, year };
}

/**
 * Generates an array of month+year options for a picker.
 */
export function getMonthYearOptions(yearsBack: number = 0, yearsForward: number = 4) {
  const currentYear = new Date().getFullYear();
  const startYear = currentYear - yearsBack;
  const endYear = currentYear + yearsForward;

  const years = Array.from({ length: endYear - startYear + 1 }, (_, i) => startYear + i);

  return years.flatMap((year) =>
    MONTHS.map((month) => ({
      value: createMonthYearValue(month, year),
      label: getMonthYearDisplay(month, year),
    })),
  );
}
